import React, { useCallback, useEffect, useState } from 'react';
import { Box, useTheme, Button, Stack } from '@mui/material';
import {
  ReactFlow,
  Node,
  Edge,
  Connection,
  Background,
  Controls,
  useReactFlow,
  ReactFlowProvider,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { CheckIcon, TrashIcon, FloppyDiskIcon } from '@phosphor-icons/react';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RootState } from '../../utils/redux';
import {
  StageData,
  setNodes,
  setNodesWithoutUnsaved,
  setEdges,
  addNode,
  updateNode,
  removeNode,
  addEdge as addEdgeAction,
  setSelectedNode,
  setConfigPanelOpen,
  setIsSaving,
  resetFlow,
  setFlowData,
  setHasUnsavedChanges,
} from '../../utils/redux/slices/callStagesFlowSlice';
import FlowSidebar from '../../components/dashboard/CallStagesFlow/FlowSidebar';
import StageNode from '../../components/dashboard/CallStagesFlow/StageNode';
import CustomToolsEdge from '../../components/dashboard/CallStagesFlow/CustomToolsEdge';
import StageConfigPanel from '../../components/dashboard/CallStagesFlow/StageConfigPanel';
import ConfirmationModal from '../../components/modals/CofirmationModal';
import {
  saveConversationFlowApi,
  getConversationFlowsApi,
} from '../../utils/apis';
import { useParams, useNavigate } from 'react-router-dom';
import { ISaveConversationFlow } from '../../schemas/apis/interface';
import CallStagesFlowSkeleton from '../../components/dashboard/CallStagesFlow/CallStagesFlowSkeleton';
import { useAlert } from '../../hooks/useAlert';
import MessagePopup from '../../components/common/MessagePopup';
import CommitMessageModal from '../../components/modals/CommitMessageModal';

const nodeTypes = {
  stageNode: StageNode,
};

const edgeTypes = {
  customToolsEdge: CustomToolsEdge,
};

interface CallStagesFlowProps {
  showSidebar?: boolean;
  agentId?: string;
  readOnly?: boolean;
}

const CallStagesFlowContent: React.FC<CallStagesFlowProps> = ({
  showSidebar = true,
  agentId: providedAgentId,
  readOnly = false,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { alert, setAlert, showAlert } = useAlert();

  const navigate = useNavigate();
  const { fitView } = useReactFlow();
  const { agentId: paramAgentId } = useParams();
  const queryClient = useQueryClient();
  // Use provided agentId or fall back to params
  const agentId = providedAgentId || paramAgentId;

  // Local state for confirmation modal
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<
    (() => void) | null
  >(null);

  // Add state to track initial loading
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Add state for reset confirmation modal
  const [showResetModal, setShowResetModal] = useState(false);

  // Add state for commit message modal
  const [showCommitModal, setShowCommitModal] = useState(false);

  const {
    nodes,
    edges,
    selectedNode,
    isConfigPanelOpen,
    isSaving,
    hasUnsavedChanges,
  } = useSelector((state: RootState) => state.callStagesFlow);

  // Prevent browser navigation when there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // React Query to fetch existing conversation flows
  const { data: flowsData, isLoading: isLoadingFlows } = useQuery({
    queryKey: ['conversationFlows', agentId],
    queryFn: () => getConversationFlowsApi(agentId!),
    enabled: !!agentId,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 30,
  });

  // API call to save conversation flow to the database
  const { mutate: saveConversationFlowMutation, isPending: isSavingFlow } =
    useMutation({
      mutationFn: saveConversationFlowApi,
      onSuccess: () => {
        dispatch(setHasUnsavedChanges(false));
        dispatch(setIsSaving(false));
        setShowConfirmationModal(false);
        showAlert('Call stages flow saved successfully!', 'success');
        queryClient.invalidateQueries({
          queryKey: ['conversationFlows', agentId],
        });

        if (pendingNavigation) {
          pendingNavigation();
          setPendingNavigation(null);
        }
      },
      onError: error => {
        console.error('Error saving conversation flow:', error);
        showAlert('Failed to save call stages. Please try again.', 'error');
        dispatch(setIsSaving(false));
      },
    });

  // Load existing flow data when component mounts
  useEffect(() => {
    if (flowsData?.data?.data) {
      setIsInitialLoading(true);

      if (flowsData.data.data.length > 0) {
        // Load existing flow data
        const latestFlow = flowsData.data.data[0];
        const flowNodes = latestFlow.data.nodes.map((node: any) => ({
          ...node,
          selected: false,
        }));
        const flowEdges = latestFlow.data.edges.map((edge: any) => ({
          ...edge,
          selected: false,
          type: 'customToolsEdge',
          data: { ...edge.data, showSidebar }, // Include showSidebar in edge data
        }));

        dispatch(setFlowData({ nodes: flowNodes, edges: flowEdges }));

        // Fit view after a short delay to ensure nodes are rendered
        // Use longer delay for read-only mode to ensure proper container sizing
        const delay = readOnly ? 300 : 100;
        setTimeout(() => {
          fitView({
            padding: readOnly ? 0.2 : 0.1,
            includeHiddenNodes: false,
            minZoom: 0.5,
            maxZoom: 1.5,
          });
          // Mark initial loading as complete after fitView
          setTimeout(() => setIsInitialLoading(false), 100);
        }, delay);
      } else {
        // Reset to initial state when data is empty
        dispatch(resetFlow());
        setIsInitialLoading(false);
      }
    }
  }, [flowsData, dispatch, fitView, readOnly, showSidebar]);

  // Modified node change handler to track unsaved changes
  const onNodesChange = useCallback(
    (changes: any) => {
      // Handle node changes (position, selection, etc.)
      const updatedNodes = nodes.map(node => {
        const change = changes.find((c: any) => c.id === node.id);
        if (change) {
          if (change.type === 'position' && change.position) {
            return { ...node, position: change.position };
          }
          if (change.type === 'select') {
            return { ...node, selected: change.selected };
          }
        }
        return node;
      });

      // Check if there are actual position changes
      const hasPositionChanges = changes.some(
        (c: any) => c.type === 'position'
      );

      if (hasPositionChanges) {
        if (isInitialLoading) {
          // During initial loading, update nodes without marking as unsaved
          dispatch(setNodesWithoutUnsaved(updatedNodes));
        } else {
          // During normal operation, mark as unsaved
          dispatch(setNodes(updatedNodes));
        }
      } else {
        // For non-position changes (like selection), don't mark as unsaved
        dispatch(setNodesWithoutUnsaved(updatedNodes));
      }
    },
    [nodes, dispatch, isInitialLoading]
  );
  const onEdgesChange = useCallback(
    (changes: any) => {
      // Handle edge changes
      let updatedEdges = [...edges];

      changes.forEach((change: any) => {
        if (change.type === 'select') {
          updatedEdges = updatedEdges.map(edge =>
            edge.id === change.id
              ? { ...edge, selected: change.selected }
              : edge
          );
        } else if (change.type === 'remove') {
          updatedEdges = updatedEdges.filter(edge => edge.id !== change.id);
        }
      });

      dispatch(setEdges(updatedEdges));
    },
    [edges, dispatch]
  );

  const onConnect = useCallback(
    (params: Edge | Connection) => {
      const newEdge = {
        id: `e${params.source}-${params.target}`,
        source: params.source,
        target: params.target,
        sourceHandle: params.sourceHandle,
        targetHandle: params.targetHandle,
        type: 'customToolsEdge', // Use the custom edge type
        data: { showSidebar }, // Pass showSidebar to edge data
      };
      dispatch(addEdgeAction(newEdge));
    },
    [dispatch, showSidebar]
  );

  const onAddStage = useCallback(
    (stageData: Partial<StageData>) => {
      const newNode: Node<StageData> = {
        id: `stage-${Date.now()}`,
        type: 'stageNode',
        position: {
          x: Math.random() * 400 + 100,
          y: Math.random() * 300 + 200,
        },
        data: {
          ...stageData,
          id: `stage-${Date.now()}`,
          stageName: stageData.stageName || 'New Stage',
          systemPrompt:
            stageData.systemPrompt || 'Enter your system prompt here...',
          voice: stageData.voice || 'default',
          temperature: stageData.temperature || 0.7,
          tools: stageData.tools || [],
          languageHint: stageData.languageHint || 'en',
          initialMessage:
            stageData.initialMessage || 'Hello! How can I help you today?',
        },
      };

      dispatch(addNode(newNode));
    },
    [dispatch]
  );

  const onNodeClick = useCallback(
    (event: React.MouseEvent, node: Node) => {
      console.log(event);
      dispatch(setSelectedNode(node as Node<StageData>));
      dispatch(setConfigPanelOpen(true));
    },
    [dispatch]
  );

  const onUpdateNode = useCallback(
    (nodeId: string, updatedData: Partial<StageData>) => {
      dispatch(updateNode({ nodeId, updatedData }));
    },
    [dispatch]
  );

  const onDeleteNode = useCallback(
    (nodeId: string) => {
      dispatch(removeNode(nodeId));
    },
    [dispatch]
  );

  // Handle navigation back from sidebar - only available when sidebar is shown
  const handleNavigateBack = useCallback(() => {
    if (hasUnsavedChanges) {
      // Set pending navigation and show confirmation modal
      setPendingNavigation(() => () => navigate(-1));
      setShowConfirmationModal(true);
    } else {
      // Navigate directly if no unsaved changes
      navigate(-1);
    }
  }, [hasUnsavedChanges, navigate]);

  // Update handleSave to show the commit modal instead of saving directly
  const handleSave = () => {
    if (!agentId) {
      showAlert('Please select an agent first', 'error');
      return;
    }
    setShowCommitModal(true);
  };

  // New: handle the actual save after commit message is provided
  const handleConfirmCommit = (commitmessage: string) => {
    setShowCommitModal(false);

    dispatch(setIsSaving(true));

    // Create a mapping from node ID to stage name
    const nodeIdToStageName = new Map<string, string>();
    nodes.forEach(node => {
      nodeIdToStageName.set(node.id, node.data.stageName);
    });

    // Function to compute connected stages for a node
    const getConnectedStages = (nodeId: string) => {
      const parentStages: { stageName: string; nodeId: string }[] = [];
      const childStages: { stageName: string; nodeId: string }[] = [];

      edges.forEach(edge => {
        if (edge.target === nodeId) {
          // This node is a target, so the source is a parent
          const parentStageName = nodeIdToStageName.get(edge.source);
          if (parentStageName) {
            parentStages.push({
              stageName: parentStageName,
              nodeId: edge.source,
            });
          }
        }
        if (edge.source === nodeId) {
          // This node is a source, so the target is a child
          const childStageName = nodeIdToStageName.get(edge.target);
          if (childStageName) {
            childStages.push({
              stageName: childStageName,
              nodeId: edge.target,
            });
          }
        }
      });

      return {
        parentStages: parentStages.length > 0 ? parentStages : null,
        childStages: childStages.length > 0 ? childStages : null,
      };
    };

    // Prepare complete flow data
    const flowData = {
      agentId: agentId,
      commitmessage,
      data: {
        nodes: nodes.map(node => {
          const connectedStages = getConnectedStages(node.id);

          const hasIncomingConnections = edges.some(
            edge => edge.target === node.id
          );

          const nodeData: any = {
            id: node.data.id,
            stageName: node.data.stageName,
            systemPrompt: node.data.systemPrompt,
            connectedStages,
            voice:
              typeof node.data.voice === 'string'
                ? node.data.voice === 'default'
                  ? { name: 'Default Voice', value: 'default' }
                  : { name: 'Unknown Voice', value: node.data.voice }
                : node.data.voice || {
                    name: 'Default Voice',
                    value: 'default',
                  },
            temperature: node.data.temperature,
            tools: node.data.tools || [],
            languageHint: node.data.languageHint,
          };

          if (!hasIncomingConnections) {
            nodeData.initialMessage = node.data.initialMessage;
          }

          return {
            id: node.id,
            type: node.type || 'stageNode',
            position: node.position,
            data: nodeData,
          };
        }),
        edges: edges.map(edge => ({
          id: edge.id,
          source: edge.source,
          target: edge.target,
          sourceHandle: edge.sourceHandle,
          targetHandle: edge.targetHandle,
          type: edge.type || 'customToolsEdge',
        })),
        savedAt: new Date().toISOString(),
      },
    };

    saveConversationFlowMutation(
      flowData as ISaveConversationFlow & { commitmessage: string }
    );
  };

  const handleReset = () => {
    const resetAction = () => {
      dispatch(resetFlow());
      showAlert('Flow reset successfully!', 'success');
      setShowResetModal(false);
    };

    if (hasUnsavedChanges) {
      resetAction();
    } else {
      // Show modal instead of window.confirm
      setShowResetModal(true);
    }
  };

  const handleConfirmReset = () => {
    dispatch(resetFlow());
    showAlert('Flow reset successfully!', 'success');
    setShowResetModal(false);
  };

  const handleCancelReset = () => {
    setShowResetModal(false);
  };

  // Handle modal cancel - discard changes and navigate
  const handleDiscardChanges = useCallback(() => {
    dispatch(setHasUnsavedChanges(false));
    setShowConfirmationModal(false);

    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    } else {
      navigate(-1);
    }
  }, [dispatch, navigate, pendingNavigation]);

  return (
    <Box
      sx={{
        width: '100%',
        height: readOnly ? 'calc(100vh - 265px)' : '100vh',
        display: 'flex',
      }}
    >
      {showSidebar && (
        <FlowSidebar
          onAddStage={onAddStage}
          hasUnsavedChanges={hasUnsavedChanges}
          onNavigateBack={handleNavigateBack}
        />
      )}

      {/* Show skeleton while loading flows */}
      {isLoadingFlows ? (
        <CallStagesFlowSkeleton />
      ) : (
        <Box sx={{ flex: 1, position: 'relative' }}>
          {/* Control Buttons - Top Right - Only show when sidebar is visible */}
          {showSidebar && (
            <Box
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                zIndex: 1000,
              }}
            >
              <Stack direction="row" spacing={1}>
                {/* Unsaved changes indicator */}
                {hasUnsavedChanges && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 2,
                      py: 1,
                      backgroundColor: theme.palette.warning.light,
                      color: theme.palette.warning.contrastText,
                      borderRadius: '6px',
                      fontSize: 12,
                      fontWeight: 500,
                    }}
                  >
                    Unsaved changes
                  </Box>
                )}

                <Button
                  variant="outlined"
                  onClick={handleReset}
                  startIcon={<TrashIcon size={16} />}
                  sx={{
                    fontSize: 14,
                    fontWeight: 500,
                    borderRadius: '6px',
                    textTransform: 'none',
                    borderColor: theme.palette.error.main,
                    color: theme.palette.error.main,
                    '&:hover': {
                      backgroundColor: theme.palette.error.main,
                      color: 'white',
                    },
                  }}
                >
                  Reset
                </Button>

                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={isSaving || isSavingFlow}
                  startIcon={<CheckIcon size={16} />}
                  sx={{
                    fontSize: 14,
                    fontWeight: 500,
                    borderRadius: '6px',
                    textTransform: 'none',
                    boxShadow: 'none',
                    transition: 'all 0.3s ease',
                    backgroundColor: theme.palette.primary.main,
                    '&:hover': {
                      boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                      backgroundColor: theme.palette.custom.tertiary,
                    },
                  }}
                >
                  {isSaving || isSavingFlow ? 'Saving...' : 'Save Flow'}
                </Button>
              </Stack>
            </Box>
          )}

          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView
            style={{ backgroundColor: theme.palette.secondary.main }}
            proOptions={{ hideAttribution: true }}
            panOnScroll={true}
            panOnScrollSpeed={1}
            panOnDrag={true}
          >
            <Background />
            <Controls />
          </ReactFlow>

          {isConfigPanelOpen && selectedNode && (
            <StageConfigPanel
              node={selectedNode}
              onClose={() => dispatch(setConfigPanelOpen(false))}
              onUpdate={onUpdateNode}
              onDelete={onDeleteNode}
              readOnly={readOnly}
            />
          )}

          {/* Confirmation Modal - only show if sidebar is present */}
          {showSidebar && (
            <>
              <ConfirmationModal
                open={showConfirmationModal}
                onClose={handleDiscardChanges}
                onConfirm={handleSave}
                isPending={isSaving || isSavingFlow}
                title="Unsaved Changes"
                description="You have unsaved changes in your call stages flow. Would you like to save them before leaving?"
                confirmButtonText="Save & Continue"
                cancelButtonText="Discard Changes"
                icon={FloppyDiskIcon}
                confirmButtonColor="primary"
              />

              <ConfirmationModal
                open={showResetModal}
                onClose={handleCancelReset}
                onConfirm={handleConfirmReset}
                isPending={false}
                title="Reset Flow"
                description="Are you sure you want to reset the flow? This will clear all nodes and connections and cannot be undone."
                confirmButtonText="Reset Flow"
                cancelButtonText="Cancel"
                icon={TrashIcon}
                confirmButtonColor="error"
              />
            </>
          )}

          {/* Message Popup */}
          <MessagePopup alert={alert} setAlert={setAlert} />

          {/* Commit Message Modal */}
          <CommitMessageModal
            open={showCommitModal}
            onClose={() => setShowCommitModal(false)}
            onConfirm={handleConfirmCommit}
            isLoading={isSaving || isSavingFlow}
          />
        </Box>
      )}
    </Box>
  );
};

const CallStagesFlow: React.FC<CallStagesFlowProps> = ({
  showSidebar = true,
  agentId,
  readOnly = false,
}) => {
  return (
    <ReactFlowProvider>
      <CallStagesFlowContent
        showSidebar={showSidebar}
        agentId={agentId}
        readOnly={readOnly}
      />
    </ReactFlowProvider>
  );
};

export default CallStagesFlow;
